import { useMemo } from "react";
import { useQuery } from "react-query";
import { AxiosError } from "axios";
import { useErrorBoundary } from "component-library";
import { authAxios as axios } from "api-generate-types/src/axios";
import { HALManager, UserManager } from "core";
import type { IProfileViewResponse, ProfileIdentityResponse } from "models";
import { useGetPublicProfile } from "pages/Profile/hooks/useGetPublicProfile";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { getApiUrl } from "services/UrlBuilder";

export interface IPronounsPronunciation {
  pronouns: string;
  pronunciation: string;
}

export const GET_PRONOUNS_PRONUNCIATION_QUERY_KEY =
  "GET_PRONOUNS_PRONUNCIATION_QUERY_KEY";

function mapIdentityToPandP(
  data: ProfileIdentityResponse
): IPronounsPronunciation {
  return {
    pronouns: data.pronouns ?? "",
    pronunciation: data.pronunciation ?? "",
  };
}

function mapPublicProfileToPandP(
  data: IProfileViewResponse | undefined
): IPronounsPronunciation {
  const identity = data?.profileIdentity;

  if (!identity) {
    return { pronouns: "", pronunciation: "" };
  }

  return mapIdentityToPandP(identity);
}

const emptyPandP: IPronounsPronunciation = {
  pronouns: "",
  pronunciation: "",
};

export function useGetPronounsPronunciation() {
  const captureError = useErrorBoundary();
  const { isPublicView } = usePublicView();
  const { data: publicProfileData, isLoading: isPublicProfileLoading } =
    useGetPublicProfile();

  const ownerId = UserManager.userId;
  const identityQueryEnabled = !isPublicView && Boolean(ownerId);

  const { data: pronounsPronunciationData, isLoading: isIdentityLoading } =
    useQuery<IPronounsPronunciation, AxiosError>(
      [GET_PRONOUNS_PRONUNCIATION_QUERY_KEY, ownerId, false],
      async () => {
        const { data: raw } = await axios.get<ProfileIdentityResponse>(
          getApiUrl(HALManager.getProfileIdentityUrl(String(ownerId)))
        );

        return mapIdentityToPandP(raw);
      },
      {
        enabled: identityQueryEnabled,
        cacheTime: 0,
        onError: captureError,
      }
    );

  const publicPandP = useMemo(
    () => mapPublicProfileToPandP(publicProfileData),
    [publicProfileData]
  );

  const isLoading = isPublicView
    ? isPublicProfileLoading
    : identityQueryEnabled && isIdentityLoading;

  const data = isPublicView
    ? publicPandP
    : (pronounsPronunciationData ?? emptyPandP);

  return {
    data,
    isLoading,
  };
}
